import { useSyncExternalStore } from "react";
import { defaultTextVariants } from "@carbon6/c6-frontend-design";
import type { SingleSelectDropdownMode, ThemeMode } from "@carbon6/c6-frontend-design";

/**
 * 앱이 쓰는 텍스트 스타일 표다. c6 `Text` 의 표시 속성은 아직 `variant` 다. 그래서 소비처는
 * `<Text variant={hatcheryTextModes[...]}>` 로 전달한다. 값은 이 파일에만 있다. c6 가 그 속성을
 * `mode` 로 바꾸면 이 파일과 JSX 속성명만 고치면 된다.
 */
export const hatcheryTextModes = defaultTextVariants;

/**
 * 앱이 쓰는 드롭다운 표시 mode 두 가지다. 모든 호출부는 c6 기본값을 쓰지 않고 여기서 고른다.
 *
 * - `default`: 값만 보이는 폼 컨트롤 외형. 라벨은 옆 요소(`Field`·표 헤더·문구)가 소유한다.
 * - `filter`: "라벨: 값" 접두어와 선택 강조가 있는 필터 바 외형. 데이터셋 필터 facet 전용이다.
 *
 * 값은 이 상수에만 적는다. 호출부의 리터럴은 eslint `no-restricted-syntax` 가 막는다.
 * 두 드롭다운의 mode union 은 같은 타입이다. 그래서 한쪽 타입으로 둘 다 검증된다.
 */
export const hatcheryDropdownModes = {
  default: "field",
  filter: "facet",
} as const satisfies Record<"default" | "filter", SingleSelectDropdownMode>;

const themeModeStorageKey = "hatchery-registry:theme-mode";

// 여러 곳(앱 셸의 ThemeProvider, 설정 모달 등)이 테마를 읽고 쓴다. 그래서 컴포넌트 로컬 state 가
// 아니라 공유 스토어 하나에 둔다. 어디서 setThemeMode 를 호출해도 모든 구독자가 같은 값을 받는다.
const themeModeListeners = new Set<() => void>();
let currentThemeMode: ThemeMode = readInitialThemeMode();

export function setThemeMode(mode: ThemeMode): void {
  if (mode === currentThemeMode) {
    return;
  }
  currentThemeMode = mode;
  try {
    window.localStorage.setItem(themeModeStorageKey, mode);
  } catch {
    // 사설 모드나 제한된 브라우저 환경에서는 Storage 를 쓸 수 없을 수 있다.
  }
  themeModeListeners.forEach((listener) => listener());
}

function subscribeThemeMode(listener: () => void): () => void {
  themeModeListeners.add(listener);
  return () => {
    themeModeListeners.delete(listener);
  };
}

export function useThemeModePreference(): readonly [ThemeMode, (mode: ThemeMode) => void] {
  const themeMode = useSyncExternalStore(
    subscribeThemeMode,
    () => currentThemeMode,
    () => currentThemeMode,
  );
  return [themeMode, setThemeMode] as const;
}

function readInitialThemeMode(): ThemeMode {
  // 스토어 초기값은 모듈 로드 시점에 한 번 계산한다. Window 가 없는 환경(테스트·SSR)에서는 기본값을 쓴다.
  if (typeof window === "undefined") {
    return "light";
  }
  const storedThemeMode = readStoredThemeMode();
  if (storedThemeMode !== null) {
    return storedThemeMode;
  }

  if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return "light";
}

function readStoredThemeMode(): ThemeMode | null {
  try {
    const value = window.localStorage.getItem(themeModeStorageKey);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}
